if (!customElements.get("tax-form-validator")) {
  class TaxFormValidator extends HTMLElement {
    constructor() {
      super();
      this.isFetching = false;
      this.canSubmit = false;
      this.isLoading = false;
      this.endpoint = null;
      this.countryCode = "";
      this.urlHighlightTimeout = null;
      this.isValidated = false;
      this.hasUnvalidatedAttributes = false;
      this.taxInputs = [];
      this.cartForm = null;
      this.nonVatPayerCheckbox = this.querySelector("#cart-b2b-non-vat-payer-input");
      this.taxFormActions = this.querySelector(".tax-form__actions");
      if (this.taxFormActions) {
        this.taxFormActions.querySelectorAll("a").forEach((action) => {
          action.addEventListener("click", this.handleTaxFormActions.bind(this));
        });
      }
      this.validateVatButton = this.querySelector(".validate-vat");
      this.validationMessage = this.querySelector(".vat-validation-message");
      if (this.validateVatButton) {
        this.validateVatButton.addEventListener("click", this.handleValidateVat.bind(this));
      }
      if (this.nonVatPayerCheckbox) {
        this.nonVatPayerCheckbox.addEventListener("change", this.handleNonVatPayerChange.bind(this));
      }
    }

    set isLoading(isLoading) {
      this._isLoading = !!isLoading;
      this.classList.remove("loading");
      if (isLoading) {
        this.classList.add("loading");
      }
    }

    get isLoading() {
      return this._isLoading;
    }

    highlightEmptyInputs(inputs = this.taxInputs) {
      inputs.forEach((input) => {
        this.toggleInputError({ input });
        if (input.value.trim().length === 0) {
          this.toggleInputError({ input, message: translations["required-input"] || "Field required" });
        }
      });
    }

    normalizeVAT(vat) {
      if (!vat) return vat;

      let cleaned = vat.trim().toUpperCase();

      cleaned = cleaned.replace(/^[A-Z]{2}/, "");
      const localNumber = this.countryCode === "HU" ? cleaned.split("-")[0] : cleaned;
      const digits = localNumber.replace(/\D/g, "");

      return `${this.countryCode}${digits}`;
    }

    validateVAT(vat) {
      const normalized = this.normalizeVAT(vat);
      const patterns = {
        HU: /^HU\d{8}$/,
        PL: /^PL\d{10}$/,
        SK: /^SK\d{10}$/,
      };
      const pattern = patterns[this.countryCode];

      return pattern ? pattern.test(normalized) : /^[A-Z]{2}\d+$/.test(normalized);
    }

    isNonVatPayer() {
      return !!this.nonVatPayerCheckbox?.checked;
    }

    getVatInput() {
      return this.taxInputs.find((input) => input.name === "attributes[dic]");
    }

    getRequiredInputs() {
      return this.taxInputs.filter((input) => !(this.isNonVatPayer() && input === this.getVatInput()));
    }

    handleNonVatPayerChange() {
      const isNonVatPayer = this.isNonVatPayer();
      this.querySelector("#cart-b2b-email-disclaimer")?.classList.toggle("hidden", isNonVatPayer);

      const vatInput = this.getVatInput();
      if (!vatInput) return;

      const vatField = vatInput.closest(".field__wrapper");
      vatField?.classList.toggle("hidden", isNonVatPayer);

      if (isNonVatPayer) {
        vatInput.value = "";
        vatInput.disabled = true;
        this.toggleInputError({ input: vatInput });
      } else if (!this.isValidated) {
        vatInput.disabled = false;
      }
    }

    generatePayload() {
      const payload = {
        company_id: "",
        company_name: "",
        email: "",
        myshopify_domain: Shopify.shop,
      };

      if (!this.isNonVatPayer()) {
        payload.vat = "";
      }

      this.taxInputs.forEach((input) => {
        const name = input.name;
        if (name === "attributes[ico]") {
          payload.company_id = input.value;
        } else if (name === "attributes[firma]") {
          payload.company_name = input.value;
        } else if (name === "attributes[email]") {
          payload.email = input.value;
        } else if (name === "attributes[dic]" && !this.isNonVatPayer()) {
          payload.vat = input.value;
        }
      });

      return payload;
    }

    getSubmitEndpoint(method = "POST") {
      const endpoint = this.endpoint || "";
      if (method === "DELETE") {
        return endpoint.replace(/\/vat-validation\/?/, "/tax-exemption/");
      }
      if (method === "POST" && this.isNonVatPayer()) {
        return endpoint.replace(/\/tax-exemption\/?/, "/vat-validation/");
      }
      return endpoint;
    }

    async sendRequest(method) {
      const payload = this.generatePayload();
      this.isFetching = true;

      try {
        const request = await fetch(this.getSubmitEndpoint(method), {
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": Shopify.locale,
          },
          method: method,
          body: JSON.stringify(payload),
        });

        let data = {};
        try {
          const contentType = request.headers.get("content-type") || "";
          const text = await request.text();
          if (text && contentType.includes("application/json")) {
            data = JSON.parse(text);
          }
        } catch (parseErr) {
          console.warn("Response parse skipped:", parseErr);
        }
        this.isFetching = false;
        return { data, ok: request.ok };
      } catch (error) {
        this.isFetching = false;
        return {
          data: { errors: { general: "Validation failed. Please try again." } },
          ok: false,
        };
      }
    }

    toggleInputError({ input, message = "" }) {
      if (!input) return;

      const wrapper = input.closest(".field__wrapper");
      if (!wrapper) return;

      const messageContainer = wrapper.querySelector(".field__message");
      if (!messageContainer) return;

      wrapper.classList.remove("field--error");
      if (message.length > 0) {
        wrapper.classList.add("field--error");
      }
      messageContainer.innerText = message;
    }

    setValidationMessage(message, isError = false) {
      if (!this.validationMessage) return;
      this.validationMessage.innerText = message || "";
      this.validationMessage.classList.remove("error", "success");
      if (message) {
        this.validationMessage.classList.add(isError ? "error" : "success");
        this.validationMessage.style.display = "block";
      } else {
        this.validationMessage.style.display = "none";
      }
    }

    toggleFormActions(show) {
      if (!this.taxFormActions) return;
      this.taxFormActions.classList.toggle("hidden", !show);
      if (show) {
        this.taxInputs.forEach((input) => {
          input.disabled = true;
        });
      }
      if (this.nonVatPayerCheckbox) {
        this.nonVatPayerCheckbox.disabled = show;
      }
    }

    displayErrors(errors) {
      const errorKeysWithInputNames = {
        email: "attributes[email]",
        company_name: "attributes[firma]",
        company_id: "attributes[ico]",
        vat: "attributes[dic]",
      };

      for (const key in errors) {
        const inputError = errors[key];
        const message = typeof inputError === "string" ? inputError : inputError.join(" ");
        const input = this.taxInputs.find((input) => input.name === errorKeysWithInputNames[key]);
        this.toggleInputError({ input, message });
      }
    }

    cloneTaxInputsToCartForm() {
      if (!this.cartForm) return;

      this.taxInputs.forEach((input) => {
        const inputInCartForm = this.cartForm.querySelector(`input[name="${input.name}"]`);

        if (inputInCartForm) {
          inputInCartForm.value = input.value;
        } else {
          const inputClone = input.cloneNode();
          inputClone.value = input.value;
          inputClone.type = "hidden";
          this.cartForm.appendChild(inputClone);
        }
      });

      const vatPayerInput = this.cartForm.querySelector('input[name="attributes[VATPayer]"]');
      const vatPayerValue = this.isNonVatPayer() ? "false" : "true";
      if (vatPayerInput) {
        vatPayerInput.value = vatPayerValue;
      } else {
        const vatPayerInputClone = document.createElement("input");
        vatPayerInputClone.name = "attributes[VATPayer]";
        vatPayerInputClone.value = vatPayerValue;
        vatPayerInputClone.type = "hidden";
        this.cartForm.appendChild(vatPayerInputClone);
      }
    }

    async persistCartAttributes() {
      const attributes = {};
      this.taxInputs.forEach((input) => {
        if (input.name && input.name.includes("attributes[")) {
          const attrName = input.name.match(/attributes\[(.*?)\]/)[1];
          attributes[attrName] = input.value;
        }
      });

      attributes["VATPayer"] = this.isNonVatPayer() ? "false" : "true";

      try {
        await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ attributes }),
        });
      } catch (error) {
        console.error("Failed to update cart:", error);
      }
    }

    async clearCartVatAttributes() {
      const attributes = { ico: "", firma: "", email: "", dic: "", VATPayer: "" };
      try {
        await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ attributes }),
        });
      } catch (error) {
        console.error("Failed to clear cart VAT attributes:", error);
      }
    }

    async handleValidateVat(e) {
      e.preventDefault();
      if (this.isFetching) return;

      this.setValidationMessage("");
      this.taxInputs.forEach((input) => this.toggleInputError({ input }));

      const requiredInputs = this.getRequiredInputs();
      const hasEmptyRequiredInput = requiredInputs.some((input) => input.value.trim().length === 0);

      if (hasEmptyRequiredInput) {
        this.highlightEmptyInputs(requiredInputs);
        this.setValidationMessage(translations["required-input"] || "Please fill all required fields", true);
        return;
      }

      const vatInput = this.getVatInput();
      if (!this.isNonVatPayer() && vatInput && vatInput.value.trim().length > 0) {
        const normalized = this.normalizeVAT(vatInput.value);

        if (!this.validateVAT(normalized)) {
          const message =
            translations["invalid-vat"] ||
            `VAT must be in format ${this.countryCode} followed by digits`;

          this.toggleInputError({ input: vatInput, message });
          this.setValidationMessage(message, true);
          return;
        }
        vatInput.value = normalized;
      }

      this.isLoading = true;
      const { data, ok } = await this.sendRequest("POST");
      this.isLoading = false;

      const hasFieldErrors = data.errors && Object.keys(data.errors).length > 0;
      const rejectedVat = !this.isNonVatPayer() && data.valid === false;
      const deductedTax = this.isNonVatPayer() && data.tax_exempt !== false;

      if (!ok || data.error_code || hasFieldErrors || rejectedVat || deductedTax) {
        this.displayErrors(data.errors || {});
        this.setValidationMessage(translations["error"] || "Validation failed. Please review the highlighted fields.", true);
        return;
      }

      this.cloneTaxInputsToCartForm();
      await this.persistCartAttributes();
      this.isValidated = true;
      this.setAttribute("data-validated", "true");
      this.canSubmit = true;
      this.setValidationMessage(translations["success"] || "VAT validated and saved.", false);
      this.toggleFormActions(true);
    }

    async handleTaxFormActions(e) {
      e.preventDefault();
      this.setValidationMessage("");
      this.isLoading = true;
      this.taxInputs.forEach((input) => {
        // remove everything except email as email is needed for DELETE method
        if (input.name !== "attributes[email]") {
          input.value = "";
        }
      });
      const { data, ok } = await this.sendRequest("DELETE");
      if (!ok || data.error_code || (data.errors && Object.keys(data.errors).length > 0)) {
        this.displayErrors(data.errors || {});
        this.setValidationMessage(translations["error"] || "Validation failed. Please try again.", true);
      } else {
        const emailInput = this.taxInputs.find((input) => input.name === "attributes[email]");
        if (emailInput) {
          emailInput.value = "";
        }
        await this.clearCartVatAttributes();
        this.isValidated = false;
        this.setAttribute("data-validated", "false");
        this.resetInputs();
      }
      this.isLoading = false;
      this.canSubmit = true;
    }

    resetInputs() {
      this.taxInputs.forEach((input) => {
        input.value = "";
        input.removeAttribute("disabled");
      });
      if (this.nonVatPayerCheckbox) {
        this.nonVatPayerCheckbox.checked = false;
        this.nonVatPayerCheckbox.disabled = false;
      }
    }

    setAccordionOpen(open) {
      const title = this.querySelector(".cart-tax-accordion__title");
      const content = this.querySelector(".cart-tax-accordion__content");
      if (!title || !content) return;

      title.classList.toggle("active", open);
      title.setAttribute("aria-expanded", open ? "true" : "false");
      content.classList.toggle("open", open);
    }

    initAccordion() {
      const title = this.querySelector(".cart-tax-accordion__title");
      const content = this.querySelector(".cart-tax-accordion__content");
      if (!title || !content) return;

      title.setAttribute("aria-expanded", content.classList.contains("open") ? "true" : "false");

      title.addEventListener("click", () => {
        this.setAccordionOpen(!content.classList.contains("open"));
      });
    }

    shouldOpenFromUrl() {
      const taxFormHashes = ["#cart-tax-form", "#tax-form"];
      const params = new URLSearchParams(window.location.search);
      const openTaxForm = [params.get("open_tax_form"), params.get("open-tax-form")];

      return (
        taxFormHashes.includes(window.location.hash.toLowerCase()) ||
        openTaxForm.some((value) => value !== null && value.toLowerCase() !== "false")
      );
    }

    openFromUrl() {
      if (!this.shouldOpenFromUrl()) return;

      this.setAccordionOpen(true);
      const highlightTarget = this.closest(".cart_tax_validator_wrapper") || this;
      highlightTarget.classList.remove("cart-tax-form--highlighted");
      void highlightTarget.offsetWidth;
      highlightTarget.classList.add("cart-tax-form--highlighted");

      window.requestAnimationFrame(() => {
        const header = document.querySelector("header-component");
        const scrollOffset = Math.max(120, (header?.getBoundingClientRect().height || 0) + 24);
        const top = this.getBoundingClientRect().top + window.pageYOffset - scrollOffset;
        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({ top: Math.max(top, 0), behavior: prefersReducedMotion ? "auto" : "smooth" });
      });

      window.clearTimeout(this.urlHighlightTimeout);
      this.urlHighlightTimeout = window.setTimeout(() => {
        highlightTarget.classList.remove("cart-tax-form--highlighted");
      }, 4000);
    }

    disconnectedCallback() {
      window.clearTimeout(this.urlHighlightTimeout);
    }

    connectedCallback() {
      this.endpoint = this.getAttribute("data-endpoint");
      this.countryCode = (this.getAttribute("data-country-code") || "").trim().toUpperCase();
      this.isValidated = this.getAttribute("data-validated") === "true";
      this.hasUnvalidatedAttributes = this.getAttribute("data-has-unvalidated-attributes") === "true";
      if (!this.endpoint) return;

      this.initAccordion();

      const cartFormSelector = this.getAttribute("data-cart-form-selector");

      if (cartFormSelector) {
        this.cartForm = document.querySelector(cartFormSelector);
      }

      if (!this.cartForm) {
        const parentContainer = this.closest('[id*="cart"], [class*="cart"]');
        if (parentContainer) {
          this.cartForm = parentContainer.querySelector('form[action*="/cart"]') || parentContainer.querySelector("form");
        }

        if (!this.cartForm) {
          this.cartForm = this.closest("form") || document.querySelector('form[action="/cart"]');
        }
      }

      // cartForm is optional — attributes are persisted via /cart/update.js independently
      this.taxInputs = Array.from(this.querySelectorAll(".tax-input"));
      this.handleNonVatPayerChange();

      this.taxInputs.forEach((input) => {
        input.addEventListener("input", () => {
          this.toggleInputError({ input });
          this.setValidationMessage("");
        });
      });

      if (!this.isValidated && this.hasUnvalidatedAttributes) {
        this.clearCartVatAttributes();
      }

      this.openFromUrl();
    }
  }

  customElements.define("tax-form-validator", TaxFormValidator);
}
